/*
 * Team and current-user context
 * Features: Loads authenticated CRM identity and memberships with auth refresh handling.
 * Implementation: Note access level is independent of team membership and comes from /who.
 * Recent changes: Exposes currentAccessLevel for note audience and post-save access explanations.
 * Keeps recognized users signed in when their identity row has no team membership.
 */
import React, { createContext, useContext, useState, useEffect } from 'react';
import { request } from '../services/dataService';
import { API_ENDPOINTS } from '../constants/apiEndpoints';
import { isAuthenticated, isAuthorized as checkAuthorized } from '../services/authService';
import { isAccessLevel, type AccessLevel } from '../constants/noteVisibilityConstants';

interface Team {
  userId: number;
  email: string;
  teamId: number;
  teamName: string;
}

interface TeamContextType {
  teams: Team[];
  isLoading: boolean;
  currentUserId: number | null;
  currentAccessLevel: AccessLevel | null;
  hasTeams: boolean;  // Property to indicate if user has teams
}

const TeamContext = createContext<TeamContextType>({
  teams: [],
  isLoading: true,
  currentUserId: null,
  currentAccessLevel: null,
  hasTeams: false
});

export const TeamProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [teams, setTeams] = useState<Team[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentUserId, setCurrentUserId] = useState<number | null>(null);
  const [currentAccessLevel, setCurrentAccessLevel] = useState<AccessLevel | null>(null);
  const [hasTeams, setHasTeams] = useState<boolean>(false);
  // States for tracking auth refreshes and timing
  const [authRefreshCount, setAuthRefreshCount] = useState(0);
  const [lastRefreshTime, setLastRefreshTime] = useState(0);

  const clearTeamState = () => {
    setTeams([]);
    setCurrentUserId(null);
    setCurrentAccessLevel(null);
    setHasTeams(false);
  };

  // Function to load teams data with retry
  const loadTeamsAndUser = async () => {
    // Check if user is authenticated and authorized before attempting to fetch teams
    const authenticated = isAuthenticated();
    const authorized = checkAuthorized();

    // If not authenticated or not authorized, don't try to load teams
    if (!authenticated || !authorized) {
      clearTeamState();
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    const MAX_RETRIES = 2;
    let retryCount = 0;

    try {
      while (retryCount <= MAX_RETRIES) {
        try {
          // User is authenticated and authorized, fetch teams
          let response;
          try {
            response = await request(API_ENDPOINTS.GET_AUTH_WHO);
          } catch (authError) {
            // If it's an auth error (401/403), don't retry
            if (authError instanceof Error &&
              (authError.message.includes('401') || authError.message.includes('403'))) {
              console.error('🔒 Authentication error - not retrying:', authError.message);
              clearTeamState();
              return; // Exit without retrying
            }
            // For other errors, let the retry logic handle it
            throw authError;
          }

          if (!response || response.length === 0) {
            const message = `⚠️ No CRM user found (attempt ${retryCount + 1}/${MAX_RETRIES + 1})`;

            // An empty response has no CRM identity; a row with a null team is still a valid user.
            throw new Error(message);
          }

          const identity = response[0];
          const userTeams = response.filter((team: Team) => Number.isInteger(team.teamId) && team.teamId > 0);
          setTeams(userTeams);
          setCurrentUserId(identity.userId);
          setCurrentAccessLevel(isAccessLevel(identity.accessLevel) ? identity.accessLevel : null);
          setHasTeams(userTeams.length > 0);
          return; // Success - exit retry loop
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Unknown error';
          console.error(`❌ Failed to load teams (attempt ${retryCount + 1}/${MAX_RETRIES + 1}):`, errorMessage);

          if (retryCount === MAX_RETRIES) {
            // Last attempt failed, clear teams
            clearTeamState();
            throw error; // Re-throw to be caught by outer try-catch
          }

          // Wait before retry with exponential backoff
          const delay = Math.min(1000 * Math.pow(2, retryCount), 4000);
          await new Promise(resolve => setTimeout(resolve, delay));
          retryCount++;
        }
      }
    } catch (error) {
      console.error('❌ All retry attempts failed:', error);
      // Set empty state after all retries fail
      clearTeamState();
    } finally {
      setIsLoading(false);
    }
  };

  // Listen for auth refresh events with debounce
  useEffect(() => {
    let refreshTimeout: ReturnType<typeof setTimeout>;

    const scheduleRefresh = () => {
      const now = Date.now();
      const timeSinceLastRefresh = now - lastRefreshTime;

      // Debounce - ignore refreshes that come too quickly
      if (timeSinceLastRefresh < 2000) {
        return;
      }

      clearTimeout(refreshTimeout);

      // Small delay to batch rapid refresh events
      refreshTimeout = setTimeout(() => {
        setLastRefreshTime(now);
        setAuthRefreshCount(prev => prev + 1);
      }, 500);
    };

    const handleAuthRefreshMessage = (event: MessageEvent) => {
      if (event.data?.type !== 'AUTH_REFRESH') {
        return;
      }

      scheduleRefresh();
    };

    const handleAuthRefreshEvent = () => {
      scheduleRefresh();
    };

    // Add event listeners for auth refresh from both service worker messages and in-app updates.
    window.addEventListener('message', handleAuthRefreshMessage);
    window.addEventListener('AUTH_REFRESH', handleAuthRefreshEvent);

    // Clean up
    return () => {
      window.removeEventListener('message', handleAuthRefreshMessage);
      window.removeEventListener('AUTH_REFRESH', handleAuthRefreshEvent);
      clearTimeout(refreshTimeout);
    };
  }, [lastRefreshTime]);

  // Load teams initially and when auth refreshes
  useEffect(() => {
    loadTeamsAndUser();
  }, [authRefreshCount]);

  return (
    <TeamContext.Provider value={{ teams, isLoading, currentUserId, currentAccessLevel, hasTeams }}>
      {children}
    </TeamContext.Provider>
  );
};

export const useTeams = () => useContext(TeamContext);
